import React, { FC, useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import ModalWrapper from '../Modals/ModalWrapper';
import { Backdrop, Box, Button, Checkbox, Chip, FormControl, ListItemText, MenuItem, Select, TextField, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import useDataArea from '../../hooks/useDataArea';
import useDataPermission from '../../hooks/useDataPermission';
import useDataCompanyUsers from '../../hooks/useDataCompanyUsers';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import { useNavigate } from 'react-router-dom';
import URLS from '../../enums/route';
import { useTour } from '../../provider/TourGuide';
import introJs from 'intro.js';
import { BussniseTour2 } from '../../enums/tourEnums';
import { useAuth } from '../../store/auth.store';
import { usePermissionStore } from '../../store/permission.store';

interface CreateModalProps {
    openModal: boolean;
    setOpenModal: (value: boolean) => void;
}

interface IFormInput {
    fullName: string;
    phone: string;
    areas: string[];
    selectedPermission: number | null;
}

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;

const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250,
        },
    },
};

const CreateModal: FC<CreateModalProps> = ({ openModal, setOpenModal }) => {
    const { data } = useDataArea();
    const { user } = useAuth()
    const { data: Tags } = useDataPermission();
    const { onExitBussnies, bussniesNumber ,  setBussniesNumber } = useTour()
    const { create } = useDataCompanyUsers();
    const navigate = useNavigate()
    const {
        fullName,
        setFullName,
        phone,
        setPhone,
        areas,
        setAreas,
        selectedPermission,
        setSelectedPermissionStore,
      } = usePermissionStore();
    
      const { control, handleSubmit, watch, reset, formState: { errors } } = useForm<IFormInput>({
        defaultValues: {
          fullName,
          phone,
          areas,
          selectedPermission,
        },
        mode: 'onChange',
      });

    // const [selectedPermission, setSelectedPermission] = useState<number | null>(null);

    const handleChipClick = (item: IPermission) => {
        setSelectedPermissionStore(selectedPermission === item.id ? null : item.id);
    };

    const onSubmit = (data: IFormInput) => {
        if (data.selectedPermission != null) {
          create(data.fullName, data.phone, data.areas, data.selectedPermission);
          setOpenModal(false);
          reset(); 
        }
    };

    const formValues = watch(['fullName','phone','areas','selectedPermission']);

    useEffect(() => {
      setFullName(formValues[0]);
      setPhone(formValues[1]);
      setAreas(formValues[2] as string[]);
      setSelectedPermissionStore(formValues[3]);
    }, [
      formValues[0],
      formValues[1],
      JSON.stringify(formValues[2]),
      formValues[3],
    ]);

    const storeSnapshot = usePermissionStore(state => ({
        fullName: state.fullName,
        phone: state.phone,
        areas: state.areas,
        selectedPermission: state.selectedPermission,
    }));

    useEffect(() => {
        reset(storeSnapshot);
    }, [storeSnapshot.fullName, storeSnapshot.phone, JSON.stringify(storeSnapshot.areas), storeSnapshot.selectedPermission]);
    
  

    const handlePermissionTitle = () => {
        const obj = Tags?.find((x) => x.id == selectedPermission);
        const det= obj?.permissionDetaileds?.[0];
        const titles = [];
      
        if (det?.isAccounting)      titles.push('צפיה ב"כספים"');
        if (det?.isArchitecture)    titles.push('פתיחת הרשאות לעובדים');
        if (det?.isDeliveryView)    titles.push('צפיה בארכיון תעודות משלוח');
        if (det?.isOrderEditable)   titles.push('עדכון וביטול הזמנה');
        if (det?.isOrderable)       titles.push('ביצוע הזמנה');
        if (det?.isOrdersView)      titles.push('צפיה בארכיון הזמנות');
        if (det?.isServiceEditable) titles.push('ביטול ועריכת קריאת שירות');
        if (det?.isServiceView)     titles.push('צפיה בארכיון קריאות שירות');
        if (det?.isServiceable)     titles.push('פתיחת קריאת שירות');
      
        return titles.join(', ');  
    }
    
    useEffect(() => {
        if (bussniesNumber === 2 && user?.permission?.permissionDetaileds?.[0]?.isArchitecture) {
            setTimeout(() => {
                let didComplete = false;
                const intro = introJs()
                .onskip(() => {
                    didComplete = true;
                    onExitBussnies()
                    setBussniesNumber(0);
                  })
                .onexit(() => {
                    if (didComplete) {
                        return;
                    }
                    navigate(URLS.PERMISSION.LINK)
                })
                intro.setOptions({
                ...BussniseTour2,
                scrollToElement: false,
                disableInteraction: true,
                showBullets: false,
                });
                intro.start();
            },500)
        }
    }, 
    [bussniesNumber]);

    return (
        <ModalWrapper 
            active={openModal} 
            setActive={setOpenModal}
            component2={
                <Box sx={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <Button onClick={() => setOpenModal(false)} sx={{ height: '35px', textDecoration: 'underline' }}>
                        ביטול
                    </Button>
                    <Button 
                        onClick={handleSubmit(onSubmit)}
                        type='submit' 
                        variant='contained' 
                        sx={{ height: '35px' }}
                        endIcon={<CheckCircleOutlineRoundedIcon sx={{ height: '25px', width: '25px' }} />}
                    > 
                        שמירה
                    </Button>
                </Box>
            }
        >
            <form>
                <Box>
                    <Box>
                        <Grid container spacing={'5px'} direction="column">
                            {/* Full Name Field */}
                            <Grid container justifyContent="flex-start" alignItems="center">
                                <Grid size={2}>
                                    <Typography align="right" fontSize={'13px'} color="#154D72" fontWeight={700} lineHeight={'13px'}>
                                        שם מלא*
                                    </Typography>
                                </Grid>
                                <Grid size={10}>
                                    <Controller
                                        name="fullName"
                                        control={control}
                                        render={({ field }) => (
                                            <TextField fullWidth {...field} error={!!errors.fullName} helperText={errors.fullName ? 'Full Name is required' : ''} />
                                        )}
                                        rules={{ required: 'Full Name is required' }}
                                    />
                                </Grid>
                            </Grid>

                            {/* Phone Field */}
                            <Grid container justifyContent="flex-start" alignItems="center">
                                <Grid size={2}>
                                    <Typography align="right" fontSize={'13px'} color="#154D72" fontWeight={700} lineHeight={'13px'}>
                                        טלפון*
                                    </Typography>
                                </Grid>
                                <Grid size={10}>
                                    <Controller
                                        name="phone"
                                        control={control}
                                        render={({ field }) => (
                                            <TextField fullWidth {...field} error={!!errors.phone} helperText={errors.phone ? 'Phone is required' : ''} />
                                        )}
                                        rules={{ required: 'Phone is required' }}
                                    />
                                </Grid>
                            </Grid>

                            {/* Area Assignment */}
                            <Grid container justifyContent="flex-start" alignItems="center">
                                <Grid size={2}>
                                    <Typography align="right" fontSize={'13px'} color="#154D72" fontWeight={700} lineHeight={'13px'}>
                                        שיוך לסניף*
                                    </Typography>
                                </Grid>
                                <Grid size={10}>
                                    <FormControl sx={{ width: '100%', maxWidth: '350px' }}>
                                        <Controller
                                            name="areas"
                                            control={control}
                                            render={({ field }) => (
                                                <Select
                                                    fullWidth
                                                    multiple
                                                    value={field.value}
                                                    sx={{
                                                        '& .MuiInputBase-root':{
                                                            border: 'none !important',
                                                        },
                                                        '& .MuiOutlinedInput-notchedOutline': {
                                                            border: 'none !important',
                                                        },
                                                        '&:hover .MuiOutlinedInput-notchedOutline, &.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                            border: 'none !important',
                                                        },
                                                        border:'1px solid #80A8B6',
                                                        borderRadius:'14px',
                                                    }}
                                                    MenuProps={{
                                                        BackdropComponent: (props) => <Backdrop {...props} sx={{ background: 'none' }} />,
                                                        PaperProps: {
                                                            sx: {
                                                                borderBottomLeftRadius: '14px',
                                                                borderBottomRightRadius: '14px',
                                                            },
                                                        },
                                                    }}
                                                    onChange={e => {
                                                        const v = typeof e.target.value === 'string'
                                                          ? e.target.value.split(',')
                                                          : e.target.value;
                                                        field.onChange(v);
                                                    }}
                                                    renderValue={(selected) =>
                                                        selected
                                                            .map((code) => {
                                                                const area = data?.find((a: any) => a.code === code);
                                                                return area?.title || '';
                                                            })
                                                            .join(', ')
                                                    }
                                                >
                                                    {data?.map((area: any) => (
                                                        <MenuItem key={area.code} value={area.code}>
                                                            <Checkbox checked={field.value.includes(area.code)} />
                                                            <ListItemText primary={area.title} />
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            )}
                                        />
                                    </FormControl>
                                </Grid>
                            </Grid>
                            <Box  id={'bussnies2'}>
                                <Grid container justifyContent="flex-start" alignItems="center" sx={{mt: '20px'}}>
                                    <Grid size={3}>
                                        <Typography align="right" fontSize={'13px'} color="#154D72" fontWeight={700} lineHeight={'13px'}>
                                            תפקיד והרשאות
                                        </Typography>
                                    </Grid>
                                    <Grid size={9}>
                                        {Tags?.map((item: IPermission) => {
                                            if(bussniesNumber === 2){
                                                return (
                                                    <>
                                                    <Chip
                                                        key={item.id}
                                                        label={item.title}
                                                        clickable
                                                        onClick={() => handleChipClick(item)}
                                                        variant={3 === item.id ? 'filled' : 'outlined'}
                                                        sx={{
                                                            border: `1px solid ${item.color}`,
                                                            color: 3 === item.id ? '#fff' : item.color,
                                                            backgroundColor: 3 === item.id ? item.color : 'transparent',
                                                            margin: '2px'
                                                        }}
                                                    />
                                                    </>
                                                
                                                )
                                            } else {
                                                return (
                                                    <Chip
                                                        key={item.id}
                                                        label={item.title}
                                                        clickable
                                                        onClick={() => handleChipClick(item)}
                                                        variant={selectedPermission === item.id ? 'filled' : 'outlined'}
                                                        sx={{
                                                            border: `1px solid ${item.color}`,
                                                            color: selectedPermission === item.id ? '#fff' : item.color,
                                                            backgroundColor: selectedPermission === item.id ? item.color : 'transparent',
                                                            margin: '2px'
                                                        }}
                                                    />
                                                )
                                            }
                            
                                        })}
                                        {bussniesNumber === 2 &&
                                            <>
                                            <Chip
                                            
                                                label={'אחמ"ש'}
                                                variant={'outlined'} 
                                                style={{color: '#418CDD', borderColor:'#418CDD', marginRight:'5px'}} 
                                            />
                                            <Chip
                                                label={'מנהל רכש'}
                                                variant={'outlined'} 
                                                style={{color: '#269E36', borderColor:'#269E36', marginRight:'5px'}} 
                                            />
                                            </>
                                        
                                        }
                        
                                    </Grid>
                                </Grid>
                                <Button 
                                    variant='text'
                                    onClick={() => navigate(URLS.PERMISSION.LINK)}
                                    startIcon={
                                        <svg width="13" height="13" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <g clipPath="url(#clip0_928_23781)">
                                                <path d="M7.49741 4.16663L5.83074 2.49996M1.03906 8.9583L2.44922 8.80161C2.6215 8.78247 2.70765 8.7729 2.78816 8.74683C2.8596 8.72371 2.92758 8.69103 2.99027 8.6497C3.06092 8.60311 3.12221 8.54182 3.24478 8.41925L8.74741 2.91663C9.20764 2.45639 9.20764 1.7102 8.74741 1.24996C8.28717 0.789726 7.54098 0.789726 7.08074 1.24996L1.57812 6.75258C1.45554 6.87515 1.39425 6.93644 1.34766 7.00709C1.30633 7.06978 1.27365 7.13776 1.25053 7.20919C1.22446 7.28971 1.21489 7.37586 1.19575 7.54815L1.03906 8.9583Z" stroke="#27C9D4" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                            </g>
                                            <defs>
                                                <clipPath id="clip0_928_23781">
                                                    <rect width="10" height="10" fill="white" />
                                                </clipPath>
                                            </defs>
                                        </svg>
                                    }
                                    sx={{
                                        fontSize:'15px',
                                        fontWeight:700,
                                        lineHeight:'19px', 
                                        ml:'100px'
                                    }}
                                >
                                    יצירת/עריכת הרשאות 
                                </Button>
                            </Box>
                        </Grid>
                        
                    </Box>
                    <Typography fontSize={'13px'} lineHeight={'19px'} fontWeight={400} color="#80A8B6" sx={{ mt: '10px' }}>
                    {handlePermissionTitle()}
                    </Typography>
                </Box>
            </form>
        </ModalWrapper>
    );
};

export default CreateModal;
